/**
 * Die Gliederung des Gewerks Heizung/Kälte (KG 420/434) — an EINER Stelle.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * HERKUNFT (TH-1, 17.10.2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis TH-1 standen die Unterseiten als sechs Einzeleinträge in
 * `navigation/struktur.ts`. Nach TX-STANDARD S1.1 hat jedes Gewerk genau EINE
 * Gliederung in `seiten/<gewerk>/abschnitte.ts`; die Bahn, die das Gewerk
 * zuerst anfasst, legt sie an (für Heizung/Kälte: Bahn TH). `struktur.ts`
 * liest sie über `heizungEintraege()` ein, nach dem Muster von
 * `seiten/heizlastkuehllast/abschnitte.ts` (dort steht das Vorgehen
 * ausführlich).
 *
 * Die Pfade sind dieselben wie vorher; ein ausgelieferter Pfad wird nie
 * geändert (S1.1). Verändert hat sich nur die Gliederung: Die Zwischenzeilen
 * heißen jetzt nach dem Abschnitt und nicht mehr „berechnung“/„daten“.
 *
 * Neue Seiten kommen hier dazu, sobald es die Route gibt — Navigation
 * verweist nur auf real Vorhandenes.
 *
 * TH-2 (18.10.2026): Gliederung nach dem Paketplan (TH-Befund §7) in vier
 * Abschnitte – Erzeugung · Netz und Abgleich · Kälte · Daten. Neu ist die
 * Seite „Erzeugung und Absicherung“ (`heizung-erzeugung`). Heizflächen und
 * Dämmung bleiben Abschnitte der Seite „Rohrnetz“ (eigene Routen erst, wenn
 * TH-5/TH-7 sie brauchen). Pfade und Namen der Bestandsseiten unverändert.
 */

export interface HeizungEintrag {
  /** Der Pfad UNTER /projekte/:id/ — genau wie in App.tsx. */
  pfad: string;
  name: string;
  /** Wozu die Seite da ist. Erscheint als Titel am Baumeintrag. */
  hinweis: string;
}

export interface HeizungAbschnitt {
  name: string;
  eintraege: HeizungEintrag[];
}

export const HEIZUNG_ABSCHNITTE: HeizungAbschnitt[] = [
  {
    name: 'Erzeugung',
    eintraege: [
      {
        // Der Pfad des Fachbereichs selbst. `alleEintraege()` filtert ihn
        // heraus (Kind == Elternpfad); er steht hier, damit der Satz der
        // Seiten vollständig ist.
        pfad: 'heizung-kaelte',
        name: 'Übersicht',
        hinweis: 'Heizung, Kälte und Rückkühlung: Kreise, Anschlusspunkte '
          + 'und Registerdaten je Systemgruppe',
      },
      {
        // TH-2 (18.10.2026): Erzeugerbilanz, Kaskade, Absicherung, Ausdehnung, J15.
        pfad: 'heizung-erzeugung',
        name: 'Erzeugung und Absicherung',
        hinweis: 'Erzeugerbilanz und Kaskade, Wärmeerzeuger, Absicherung je Erzeuger, Ausdehnung '
          + 'und Absicherungsprüfung (J15) nach DIN EN 12828 – mit Vorbehalten und Normbezug',
      },
    ],
  },
  {
    name: 'Netz und Abgleich',
    eintraege: [
      {
        pfad: 'heizung-berechnung',
        name: 'Rohrnetz',
        hinweis: 'Rohrnetzberechnung je Systemgruppe: Wege, Förderhöhe, '
          + 'Heizflächen und Dämmung',
      },
      {
        // TH-4 (17.10.2026): hydraulischer Abgleich nach DIN EN 14336 / VdZ-Verfahren B.
        pfad: 'heizung-abgleich',
        name: 'Hydraulischer Abgleich',
        hinweis: 'Einstellwerte je Verbraucher, Strangregulierventile und Differenzdruckregler, '
          + 'Pumpen nach dem Abgleich und EEI — mit Annahmen und Normbezug',
      },
      {
        pfad: 'heizung-netzbruecken',
        name: 'Netzbrücken',
        hinweis: 'Lücken im Rohrnetz des Modells und Vorschläge, sie zu '
          + 'schließen — Arbeitsliste der Modellpflege',
      },
    ],
  },
  {
    name: 'Kälte',
    eintraege: [
      {
        pfad: 'kaelte',
        name: 'Kälte',
        hinweis: 'Kältebilanz (jüngster Bilanzlauf) in drei Lesarten, '
          + 'Rückkühlung, Eisspeicher, freie Kühlung — mit Randbedingungen',
      },
      {
        // TH-3 (17.10.2026): Kälte-Kern Stufe 1 und DIN EN 378.
        pfad: 'kaelte-nachweis',
        name: 'Kältenachweis (EN 378)',
        hinweis: 'Erzeugung, Teillast, n−1, Eisspeicherprofil, freie Kühlung '
          + 'und DIN EN 378 (R1234ze) — mit Annahmen und Normbezug',
      },
    ],
  },
  {
    name: 'Daten',
    eintraege: [
      {
        pfad: 'heizung-datenpflege',
        name: 'Datenpflege',
        hinweis: 'Bauteilwerte, Datenblätter und Produktreihen der '
          + 'Heizungs- und Kältebauteile',
      },
      {
        pfad: 'geraete',
        name: 'Geräteliste',
        hinweis: 'Der gemessene Bestand: Geräte aus dem Modell mit ihren '
          + 'Kennwerten',
      },
      {
        pfad: 'heizung-sollgeraete',
        name: 'Sollgeräte (Fachplaner)',
        hinweis: 'Die Geräteliste des Fachplaners gegen das Modell — was '
          + 'gebaut wird und was das Modell zeigt',
      },
    ],
  },
];
